/**
 * Contacts (`/{ws}/contacts`, logic/mail): everyone the workspace exchanges mail with, filtered by kind — Email,
 * Clients, Team (workspace members are always Team) — and searched by name, address or organization. Marking a
 * contact Client or Team sends their mail to that tab. Contacts link to projects (rows of a Projects database, mail
 * "People cards"). Online only; edits are optimistic and refetch when refused.
 */
import type { ContactKind, MailContact, MailProject, MailThread } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { mailWithSearch, matchesContact, normalizeAddress } from "../logic/mail.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ContactsViewState = {
  status: "loading" | "ready" | "failed";
  /** Every contact (the filter is applied locally). */
  all: MailContact[];
  /** The filter: undefined = everyone. */
  kind?: ContactKind;
  query: string;
  contacts: MailContact[];
  counts: Record<ContactKind | "all", number>;
  /** Project rows contacts can be linked to; loaded with `loadProjects`. */
  projects: MailProject[];
  projectsStatus: "idle" | "loading" | "ready" | "failed";
  /** The open contact's latest conversations (the detail's "Recent emails"). */
  recent?: { contactId: string; status: "loading" | "ready" | "failed"; threads: MailThread[] };
  error?: MessageDescriptor;
};

function counts(all: MailContact[]): ContactsViewState["counts"] {
  return {
    all: all.length,
    email: all.filter((c) => c.kind === "email").length,
    client: all.filter((c) => c.kind === "client").length,
    team: all.filter((c) => c.kind === "team").length,
  };
}

export class ContactsViewModel extends ViewModel<ContactsViewState> {
  constructor(private readonly ws: WorkspaceViewModel) {
    super({ status: "loading", all: [], query: "", contacts: [], counts: { all: 0, email: 0, client: 0, team: 0 }, projects: [], projectsStatus: "idle" });
  }

  /** Loads the contacts once (people cards need them; the Contacts screen refreshes on open). */
  ensureLoaded(): void {
    if (this.state.status === "ready" || this.ensuring) return;
    this.ensuring = true;
    void this.refresh().finally(() => {
      this.ensuring = false;
    });
  }

  /** One load at a time for `ensureLoaded` (every card and contact cell asks). */
  private ensuring = false;

  async loadProjects(): Promise<void> {
    if (this.state.projectsStatus === "idle" || this.state.projectsStatus === "failed") this.update({ projectsStatus: "loading" });
    try {
      const projects = await this.ws.deps.api.listMailProjects(this.ws.wsId);
      if (!this.isDisposed) this.update({ projects, projectsStatus: "ready" });
    } catch {
      if (!this.isDisposed && this.state.projectsStatus !== "ready") this.update({ projectsStatus: "failed" });
    }
  }

  /** The latest conversations with a contact in a mailbox (`with:` their address), newest first. */
  async loadRecent(contactId: string, mailboxId: string, limit = 5): Promise<void> {
    const c = this.state.all.find((x) => x.id === contactId);
    if (c === undefined) return;
    this.update({ recent: { contactId, status: "loading", threads: this.state.recent?.contactId === contactId ? this.state.recent.threads : [] } });
    try {
      const r = await this.ws.deps.api.listMailThreads(this.ws.wsId, mailboxId, { q: mailWithSearch([c.email]), limit, counts: "none" });
      if (!this.isDisposed && this.state.recent?.contactId === contactId) this.update({ recent: { contactId, status: "ready", threads: r.threads.slice(0, limit) } });
    } catch {
      if (!this.isDisposed && this.state.recent?.contactId === contactId) this.update({ recent: { contactId, status: "failed", threads: [] } });
    }
  }

  /** Adds (`on`) or takes a contact out of a project row; optimistic, put back when refused. */
  setProject(contactId: string, rowId: string, on: boolean): void {
    const before = this.state.projects;
    this.update({ projects: before.map((p) => (p.rowId !== rowId ? p : { ...p, contactIds: on ? [...p.contactIds.filter((x) => x !== contactId), contactId] : p.contactIds.filter((x) => x !== contactId) })) });
    const api = this.ws.deps.api;
    (on ? api.linkContactProject(this.ws.wsId, contactId, rowId) : api.unlinkContactProject(this.ws.wsId, contactId, rowId)).then(
      (p) => {
        if (!this.isDisposed) this.update({ projects: this.state.projects.map((x) => (x.rowId === p.rowId ? p : x)) });
      },
      () => {
        if (!this.isDisposed) this.update({ projects: before, error: msg("mail.people.projectFailed") });
      },
    );
  }

  async refresh(): Promise<void> {
    try {
      const all = await this.ws.deps.api.listContacts(this.ws.wsId);
      if (!this.isDisposed) this.derive(all, "ready");
    } catch {
      if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
    }
  }

  setKind(kind: ContactKind | undefined): void {
    const { kind: _old, ...rest } = this.state;
    void _old;
    this.setState(kind === undefined ? rest : { ...rest, kind });
    this.derive(this.state.all);
  }

  setQuery(query: string): void {
    this.update({ query });
    this.derive(this.state.all);
  }

  /** Marks a contact Email / Client / Team (members stay Team). */
  setContactKind(contactId: string, kind: ContactKind): void {
    this.optimistic(contactId, { kind, source: "manual" }, () => this.ws.deps.api.updateContact(this.ws.wsId, contactId, { kind }));
  }

  rename(contactId: string, name: string, org: string): void {
    const patch: Partial<MailContact> = {};
    if (name.trim() !== "") patch.name = name.trim();
    if (org.trim() !== "") patch.org = org.trim();
    this.optimistic(contactId, patch, () => this.ws.deps.api.updateContact(this.ws.wsId, contactId, { name: name.trim() || null, org: org.trim() || null }));
  }

  /** Adds a contact; the new contact, or undefined (with `error` set) when refused. */
  async add(email: string, name: string, kind: ContactKind): Promise<MailContact | undefined> {
    if (normalizeAddress(email) === undefined) {
      this.update({ error: msg("mail.contacts.invalidEmail") });
      return undefined;
    }
    try {
      const c = await this.ws.deps.api.createContact(this.ws.wsId, { email, kind, ...(name.trim() === "" ? {} : { name: name.trim() }) });
      this.derive([c, ...this.state.all.filter((x) => x.id !== c.id)]);
      return c;
    } catch {
      this.update({ error: msg("mail.contacts.addFailed") });
      return undefined;
    }
  }

  remove(contactId: string): void {
    const before = this.state.all;
    this.derive(before.filter((c) => c.id !== contactId));
    this.ws.deps.api.deleteContact(this.ws.wsId, contactId).catch(() => this.derive(before));
  }

  private optimistic(contactId: string, patch: Partial<MailContact>, call: () => Promise<MailContact>): void {
    this.derive(this.state.all.map((c) => (c.id === contactId ? { ...c, ...patch } : c)));
    call().then(
      (c) => this.derive(this.state.all.map((x) => (x.id === c.id ? c : x))),
      () => void this.refresh(),
    );
  }

  private derive(all: MailContact[], status?: ContactsViewState["status"]): void {
    const contacts = all.filter((c) => matchesContact(c, this.state.kind, this.state.query));
    this.update({ all, contacts, counts: counts(all), ...(status === undefined ? {} : { status }) });
  }
}
